// @ts-nocheck
import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

interface PartnerProspectFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PARTNERSHIP_TYPES = [
  { value: 'technology', label: 'Technology Integration' },
  { value: 'content', label: 'Content Partnership' },
  { value: 'distribution', label: 'Distribution Partner' },
  { value: 'strategic', label: 'Strategic Alliance' }
];

const COMPANY_SIZES = [
  { value: 'startup', label: 'Startup (1-10 employees)' },
  { value: 'small', label: 'Small (11-50 employees)' },
  { value: 'medium', label: 'Medium (51-200 employees)' },
  { value: 'large', label: 'Large (201-1000 employees)' },
  { value: 'enterprise', label: 'Enterprise (1000+ employees)' }
];

const REVENUE_RANGES = [
  { value: 'under_1m', label: 'Under $1M' },
  { value: '1m_10m', label: '$1M - $10M' },
  { value: '10m_50m', label: '$10M - $50M' },
  { value: '50m_100m', label: '$50M - $100M' },
  { value: 'over_100m', label: 'Over $100M' }
];

const TARGET_MARKETS = [
  'High School Athletes',
  'College Athletes',
  'Youth Sports',
  'Professional Sports',
  'Coaches',
  'Parents',
  'Sports Organizations',
  'Educational Institutions'
];

const PartnerProspectForm: React.FC<PartnerProspectFormProps> = ({ open, onClose, onSuccess }) => {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    partnership_type: '',
    company_size: '',
    annual_revenue_range: '',
    partnership_value_potential: '',
    strategic_fit_score: '',
    notes: '',
    target_market_overlap: [] as string[]
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) {
      toast.error('User profile not found');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('partner_prospects')
        .insert({
          sales_rep_id: profile.id,
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          contact_email: formData.contact_email || null,
          contact_phone: formData.contact_phone || null,
          partnership_type: formData.partnership_type,
          company_size: formData.company_size || null,
          annual_revenue_range: formData.annual_revenue_range || null,
          partnership_value_potential: formData.partnership_value_potential ? parseInt(formData.partnership_value_potential) : null,
          strategic_fit_score: formData.strategic_fit_score ? parseInt(formData.strategic_fit_score) : null,
          notes: formData.notes || null,
          target_market_overlap: formData.target_market_overlap.length > 0 ? formData.target_market_overlap : null
        });

      if (error) throw error;

      toast.success('Partner prospect added successfully');
      onSuccess();
    } catch (error) {
      console.error('Error adding partner prospect:', error);
      toast.error('Failed to add partner prospect');
    } finally {
      setLoading(false);
    }
  };

  const handleTargetMarketChange = (market: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      target_market_overlap: checked
        ? [...prev.target_market_overlap, market]
        : prev.target_market_overlap.filter(m => m !== market)
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Partner Prospect</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Company Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="partnership_type">Partnership Type *</Label>
                <Select
                  value={formData.partnership_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, partnership_type: value }))}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PARTNERSHIP_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_size">Company Size</Label>
                <Select
                  value={formData.company_size}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, company_size: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select size" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_SIZES.map(size => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="annual_revenue_range">Annual Revenue</Label>
                <Select
                  value={formData.annual_revenue_range}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, annual_revenue_range: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {REVENUE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>
                        {range.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Contact Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="contact_phone">Contact Phone</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Partnership Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Partnership Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="partnership_value_potential">Potential Value ($)</Label>
                <Input
                  id="partnership_value_potential"
                  type="number"
                  min="0"
                  value={formData.partnership_value_potential}
                  onChange={(e) => setFormData(prev => ({ ...prev, partnership_value_potential: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="strategic_fit_score">Strategic Fit Score (1-10)</Label>
                <Input
                  id="strategic_fit_score"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.strategic_fit_score}
                  onChange={(e) => setFormData(prev => ({ ...prev, strategic_fit_score: e.target.value }))}
                />
              </div>
            </div>
          </div>

          {/* Target Market Overlap */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Target Market Overlap</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {TARGET_MARKETS.map(market => (
                <div key={market} className="flex items-center space-x-2">
                  <Checkbox
                    id={market}
                    checked={formData.target_market_overlap.includes(market)}
                    onCheckedChange={(checked) => handleTargetMarketChange(market, checked as boolean)}
                  />
                  <Label htmlFor={market} className="text-sm">
                    {market}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Additional notes about this partnership opportunity..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Adding...' : 'Add Prospect'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PartnerProspectForm;